import { ComputedRef, onBeforeUnmount, watch } from 'vue';
import { until } from '@vueuse/core';
import {
  IAssistantHighlight,
  useAssistantStore,
} from '@/composition/assistant/use-assistant-store';
import { menuIdOf } from '@/domain/catalog/catalog-view';
import { IItem } from '@/interfaces/IItem';
import type { ICatalogPageState } from './catalog-page-state';
import type { ICatalogData } from './use-catalog-data';
import type { ICatalogUrl } from './use-catalog-url';

/** How long the rows stay highlighted (ms). */
export const HIGHLIGHT_MS = 2400;
/** A request older than this was left behind by a navigation that never arrived (ms). */
const HIGHLIGHT_MAX_AGE_MS = 15_000;
/** The list gets this long to show the rows once the filter and search clear (ms). */
const REVEAL_TIMEOUT_MS = 3_000;

export interface ICatalogHighlightDeps {
  state: ICatalogPageState;
  data: ICatalogData;
  url: ICatalogUrl;
  loaded: ComputedRef<boolean>;
  jumpToCategory: (categoryId: number) => Promise<void>;
  /** Scrolls a row of the category just jumped to into view. */
  revealRow: (itemId: number) => Promise<void>;
}

/**
 * «Ver en el menú» from the assistant (plan §9.10): once the catalog is
 * loaded, the rows it points at are brought on screen (clearing a filter or
 * search that hides them, switching to their menu), the topmost of them is
 * scrolled into view, and they are highlighted for 2.4 s.
 */
export function useCatalogHighlight(deps: ICatalogHighlightDeps) {
  const { state, data, url, loaded } = deps;
  const assistant = useAssistantStore();

  /** Accepted, waiting for the catalog to load. */
  let pending: IAssistantHighlight | null = null;
  /** Grows on every run: an older run stops at its next await. */
  let run = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  function clear() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (Object.keys(state.highlightIds).length) state.highlightIds = {};
  }

  function isShown(itemId: number): boolean {
    return data.shownSections.value.some((section) =>
      section.items.some((item) => item.id === itemId)
    );
  }

  /** The first of `ids` in display order: the topmost of those rows. */
  function topmostShown(
    ids: Record<number, true>
  ): { itemId: number; categoryId: number } | null {
    for (const section of data.shownSections.value)
      for (const item of section.items)
        if (ids[item.id])
          return { itemId: item.id, categoryId: section.category.id };
    return null;
  }

  async function show(request: IAssistantHighlight) {
    const current = ++run;
    clear();
    // Reorder mode shows no product rows (and may hold an unsaved order).
    if (state.mode === 'reorder') return;
    const items = request.itemIds
      .map((id) => data.itemById.value[id])
      .filter((item): item is IItem => !!item);
    const first = items[0];
    if (!first) return;
    const ids: Record<number, true> = {};
    for (const item of items) ids[item.id] = true;

    if (!items.every((item) => isShown(item.id))) {
      const category = data.categoryById.value.get(first.categoryId);
      const menuId = category ? menuIdOf(category) : state.menuId;
      url.revealAll(menuId);
      // Until the list settled (the debounced search too): its reset
      // scrolls to the top, which must come before the jump.
      await until(
        () =>
          state.menuId === menuId &&
          state.filter === 'all' &&
          !data.debouncedQuery.value.trim() &&
          isShown(first.id)
      ).toBe(true, { timeout: REVEAL_TIMEOUT_MS });
      if (disposed || current !== run) return;
    }

    // Its category head alone is not enough: in a long category the row can
    // sit far below it. The highlight starts once the row is on screen.
    const target = topmostShown(ids);
    if (!target) return;
    await deps.jumpToCategory(target.categoryId);
    if (disposed || current !== run) return;
    await deps.revealRow(target.itemId);
    if (disposed || current !== run) return;
    state.highlightIds = ids;
    timer = setTimeout(() => {
      timer = null;
      state.highlightIds = {};
    }, HIGHLIGHT_MS);
  }

  function runPending() {
    const request = pending;
    pending = null;
    if (request) void show(request);
  }

  // Taken (and cleared from the store) as soon as it arrives, so a later
  // visit never replays it.
  watch(
    () => assistant.highlight,
    (request) => {
      if (!request) return;
      assistant.clearHighlight(request.token);
      if (Date.now() - request.requestedAt > HIGHLIGHT_MAX_AGE_MS) return;
      pending = request;
      if (loaded.value) runPending();
    },
    { immediate: true }
  );

  watch(loaded, (isLoaded) => {
    if (isLoaded) runPending();
  });

  // Another store: nothing of the previous one survives.
  watch(
    () => data.restaurant.value?.id,
    () => {
      pending = null;
      run += 1;
      clear();
    }
  );

  onBeforeUnmount(() => {
    disposed = true;
    pending = null;
    if (timer !== null) clearTimeout(timer);
    timer = null;
  });
}
